import { type FC } from "react"
import { useParams } from "react-router"
import styled from "styled-components"

import { parseNumber } from "@forento/core/utilities/number"
import Tooltip from "@forento/ui/components/Tooltip"

import Button from "~/components/Button"
import { CloseIcon, LikeIcon } from "~/components/Icon"
import Layout, { PageBreadcrumb } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import routes from "~/utilities/routes"
import { componentBackgroundColor, componentBorderColor } from "~/utilities/styles"
import trpc, { query } from "~/utilities/trpc"

const ReviewCommentsPage: FC = () => {
	const courseId = parseNumber(useParams().courseId) ?? -1

	const course = query.course.get.useQuery(courseId)
	const comments = query.course.listUnapprovedComments.useQuery({ courseId })

	return (
		<Layout>
			<PageBreadcrumb
				title="Review comments"
				path={[
					{ title: "Courses", link: routes.course.index() },
					{ title: course.data?.title ?? "...", link: routes.course.detail(courseId) },
				]}
			/>
			{comments.error ? (
				<p>Failed to load comments.</p>
			) : !comments.data ? (
				<PartialLoadingPage />
			) : (
				<Comments>
					{comments.data.map(comment => (
						<Comment key={`${comment.type}-${comment.id}`}>
							<div>
								<CommentAuthor>
									{comment.user.firstName} {comment.user.lastName}
								</CommentAuthor>
								<CommentText dangerouslySetInnerHTML={{ __html: comment.htmlContent }} />
							</div>
							<Actions>
								<Tooltip tooltip="Approve">
									<Action
										onClick={async () => {
											try {
												if (comment.type === "comment") {
													await trpc.course.approveComment.mutate(comment.id)
												} else {
													await trpc.course.approveCommentReply.mutate(comment.id)
												}
											} finally {
												await comments.refetch()
											}
										}}
									>
										<LikeIcon />
									</Action>
								</Tooltip>
								<Tooltip tooltip="Reject">
									<Action
										onClick={async () => {
											if (comment.type === "comment") {
												await trpc.course.deleteComment.mutate(comment.id)
											} else {
												await trpc.course.deleteCommentReply.mutate(comment.id)
											}
											await comments.refetch()
										}}
									>
										<CloseIcon />
									</Action>
								</Tooltip>
							</Actions>
						</Comment>
					))}
				</Comments>
			)}
		</Layout>
	)
}

const Comments = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const Comment = styled.div`
	background-color: ${componentBackgroundColor};
	border: 1px solid ${componentBorderColor};
	border-radius: 8px;
	padding: 22px;
	display: flex;
	justify-content: space-between;
	gap: 16px;
`

const CommentAuthor = styled.h2`
	font-weight: 600;
	font-size: 18px;
	text-align: left;
`

const CommentText = styled.p`
	font-size: 16px;
	text-align: left;
	a {
		color: #007bff;
	}
`

const Actions = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
`

const Action = styled(Button)`
	width: 24px;
	height: 24px;
`

export default ReviewCommentsPage
